// SHIFT1 (docs/build-briefs/SHIFT1-unpaid-tabs-at-sign-out.md): the pure rules behind the sign-out sheet and
// the closeout line. No DOM and no state, so a unit test proves them directly.

export interface SignOutTab {
  openedByUserId: string | null;
  /** TAB3's paid state: true or false once the figures loaded, absent before. Absent is never unpaid. */
  paid: boolean | undefined;
  /** Drinks on the tab. A tab with none owes nothing and is never counted; absent (not loaded) is not counted. */
  drinkCount: number | undefined;
}

/** Tabs the signed-in user opened that are known to be unpaid and have at least one drink. */
export function countUnpaidTabsOpenedBy(
  tabs: readonly SignOutTab[],
  userId: string | undefined,
): number {
  if (userId === undefined) {
    return 0;
  }
  return tabs.filter(
    (tab) => tab.openedByUserId === userId && tab.paid === false && (tab.drinkCount ?? 0) > 0,
  ).length;
}

export function unpaidTabsTitle(count: number): string {
  return `${count} unpaid ${count === 1 ? "tab" : "tabs"}`;
}

export interface OpenTabOpenerRow {
  openedByUserId: string | null;
  openedByName: string | null;
}

export interface OpenTabsByOpener {
  key: string;
  name: string;
  count: number;
}

export const NO_OPENER_LABEL = "Opener not recorded";

/** One entry per opener with how many of the tabs still open at day close they opened. Most first, then by
 * name; tabs with no recorded opener are grouped last under one label. */
export function groupOpenTabsByOpener(rows: readonly OpenTabOpenerRow[]): OpenTabsByOpener[] {
  const byKey = new Map<string, OpenTabsByOpener>();
  for (const row of rows) {
    const key = row.openedByUserId ?? "";
    const existing = byKey.get(key);
    if (existing !== undefined) {
      existing.count += 1;
    } else {
      byKey.set(key, {
        key,
        name: row.openedByUserId === null ? NO_OPENER_LABEL : (row.openedByName ?? "Unknown"),
        count: 1,
      });
    }
  }
  return [...byKey.values()].sort((a, b) => {
    if (a.key === "" || b.key === "") {
      return a.key === "" ? 1 : -1;
    }
    return b.count - a.count || a.name.localeCompare(b.name);
  });
}

export interface UnpaidTabLine {
  label: string;
  /** What the tab shows as its total, in cents; absent while the figures are not loaded (then the line names no amount). */
  totalCents: number | undefined;
}

/** LOUNGERMAJ1 (lounger-19): what the sign-out sheet says about the tabs left open, and what signing out does to them:
 * the tab and its amount, and that it stays open. Up to two tabs are named; the rest are counted. `money` formats cents. */
export function unpaidTabsSentence(
  tabs: readonly UnpaidTabLine[],
  money: (cents: number) => string,
): string {
  const named = tabs.slice(0, 2).map((tab) =>
    tab.totalCents === undefined ? tab.label : `${tab.label}, ${money(tab.totalCents)}`,
  );
  const more = tabs.length - named.length;
  const list = more > 0 ? `${named.join("; ")} and ${more} more` : named.join("; ");
  return `${list} ${tabs.length === 1 ? "is" : "are"} still open. ${tabs.length === 1 ? "It stays" : "They stay"} open after you sign out.`;
}
